import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef, useState, useEffect, useMemo } from 'react';
import type { HierarchyItem, DrillLevel, DemoFilters } from '../../../api/wattelzce-demo';
import { getTopZones, getTopModulesInZone, getEquipmentInModule } from '../../../api/wattelzce-demo';
import { useI18n } from '../../../i18n';

interface TopModulesChartProps {
  /** @deprecated No longer used - chart fetches its own data via drill-down */
  data?: unknown[];
  loading?: boolean;
  filters?: DemoFilters;
}

// Drill-down navigation state
interface DrillState {
  level: DrillLevel;
  parentId?: string;
  parentName?: string;
  breadcrumb: Array<{ id: string; name: string; level: DrillLevel }>;
}

function TopModulesChart({ loading, filters = {} }: TopModulesChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t, locale, language } = useI18n();
  
  // Drill-down state
  const [drillState, setDrillState] = useState<DrillState>({
    level: 'zone',
    breadcrumb: [],
  });
  const [chartData, setChartData] = useState<HierarchyItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Store drill-down handler in ref to avoid closure issues with Highcharts
  const drillDownRef = useRef<(item: HierarchyItem) => void>(() => {});

  // Memoize filters to prevent unnecessary recalculations
  const stableFilters = useMemo(() => filters, [JSON.stringify(filters)]);

  // Load data based on current drill level
  useEffect(() => {
    if (loading) return;
    
    try {
      let newData: HierarchyItem[];
      
      switch (drillState.level) {
        case 'zone':
          newData = getTopZones(stableFilters, 10);
          break;
        case 'module':
          newData = drillState.parentId 
            ? getTopModulesInZone(stableFilters, drillState.parentId, 10)
            : [];
          break;
        case 'equipment':
          newData = drillState.parentId
            ? getEquipmentInModule(stableFilters, drillState.parentId, 10)
            : [];
          break;
        default:
          newData = getTopZones(stableFilters, 10);
      }
      
      setChartData(newData);
      setIsInitialized(true);
    } catch (error) {
      console.error('Error loading drill-down data:', error);
      setChartData([]);
      setIsInitialized(true);
    }
  }, [drillState.level, drillState.parentId, stableFilters, loading, language]); // language : libellés « Zone n » traduits au calcul

  // Update drill-down ref when state changes
  useEffect(() => {
    drillDownRef.current = (item: HierarchyItem) => {
      if (!item.hasChildren) return;
      
      const nextLevel: DrillLevel = item.level === 'zone' ? 'module' : 'equipment';
      
      setDrillState(prev => ({
        level: nextLevel,
        parentId: item.id,
        parentName: item.name,
        breadcrumb: [...prev.breadcrumb, { id: item.id, name: item.displayLabel, level: item.level }],
      }));
    };
  }, []);

  // Handle drill-up (back navigation)
  const handleDrillUp = () => {
    setDrillState(prev => {
      if (prev.breadcrumb.length === 0) return prev;
      
      const newBreadcrumb = prev.breadcrumb.slice(0, -1);
      const parentItem = newBreadcrumb[newBreadcrumb.length - 1];
      
      if (!parentItem) {
        // Back to root level
        return {
          level: 'zone',
          breadcrumb: [],
        };
      }
      
      return {
        level: parentItem.level === 'zone' ? 'module' : 'zone',
        parentId: parentItem.id,
        parentName: parentItem.name,
        breadcrumb: newBreadcrumb,
      };
    });
  };

  // Reset to top level
  const handleReset = () => {
    setDrillState({
      level: 'zone',
      breadcrumb: [],
    });
  };

  if (loading || !isInitialized) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Handle empty data state
  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96 flex items-center justify-center">
        <p className="text-gray-500">{t('noData')}</p>
      </div>
    );
  }

  // Get title based on current level
  const getLevelTitle = () => {
    switch (drillState.level) {
      case 'zone': return t('topModules');
      case 'module': return `${t('topModules')} - ${drillState.parentName}`;
      case 'equipment': return `${t('topModules')} - ${drillState.parentName}`;
      default: return t('topModules');
    }
  };

  const getLevelSubtitle = () => {
    switch (drillState.level) {
      case 'zone': return `${t('topModulesSubtitle')} (${t('zones')})`;
      case 'module': return `${t('topModulesSubtitle')} (${t('modules')})`;
      case 'equipment': return `${t('topModulesSubtitle')} (${t('equipment')})`;
      default: return t('topModulesSubtitle');
    }
  };

  const categories = chartData.map(d => d.displayLabel);
  const colors = [
    '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca',
    '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0',
  ];
  const energyData = chartData.map((d, index) => {
    return {
      y: d.energy,
      color: colors[index % colors.length],
      name: d.name,
      displayLabel: d.displayLabel,
      cost: d.cost,
      co2: d.co2,
      hasChildren: d.hasChildren,
      itemData: d,
    };
  });

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      height: 350,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: getLevelTitle(),
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: getLevelSubtitle(),
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '10px',
          color: '#374151',
        },
        rotation: -45,
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      min: 0,
      title: {
        text: `${t('energy')} (kWh)`,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value:,.0f}',
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      useHTML: true,
      formatter: function() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx = this as any;
        const point = ctx.point?.options || {};
        const drillHint = point.hasChildren 
          ? `<br/><span style="color: #3b82f6; font-size: 11px;">🔍 ${t('viewDetails')}</span>` 
          : '';
        return `
          <div style="padding: 8px;">
            <strong>${point.name || ctx.x}</strong><br/>
            <span style="color: #059669;">${t('energy')}:</span> ${point.y?.toLocaleString(locale, {maximumFractionDigits: 1})} kWh<br/>
            <span style="color: #2563eb;">${t('cost')}:</span> ${point.cost?.toLocaleString(locale, {maximumFractionDigits: 2})} €<br/>
            <span style="color: #dc2626;">CO₂:</span> ${point.co2?.toLocaleString(locale, {maximumFractionDigits: 1})} kg
            ${drillHint}
          </div>
        `;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        borderRadius: 4,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '{point.y:,.0f}',
          style: {
            fontSize: '9px',
            fontWeight: '500',
            color: '#374151',
          },
        },
        point: {
          events: {
            click: function() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const point = this as any;
              if (point.options?.itemData?.hasChildren) {
                // Use ref to avoid closure issues with React hooks
                drillDownRef.current(point.options.itemData);
              }
            },
          },
        },
      },
    },
    series: [{
      type: 'column',
      name: t('energy'),
      data: energyData,
      colorByPoint: true,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Breadcrumb navigation */}
      {drillState.breadcrumb.length > 0 && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <button
            onClick={handleReset}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {t('zones')}
          </button>
          {drillState.breadcrumb.map((item, index) => (
            <span key={item.id} className="flex items-center gap-2">
              <span className="text-gray-400">→</span>
              <button
                onClick={() => {
                  // Navigate to this level
                  if (index === drillState.breadcrumb.length - 1) return;
                  setDrillState(prev => ({
                    level: item.level === 'zone' ? 'module' : 'equipment',
                    parentId: item.id,
                    parentName: item.name,
                    breadcrumb: prev.breadcrumb.slice(0, index + 1),
                  }));
                }}
                className={`${
                  index === drillState.breadcrumb.length - 1 
                    ? 'text-gray-700 font-medium' 
                    : 'text-blue-600 hover:text-blue-800 hover:underline'
                }`}
              >
                {item.name}
              </button>
            </span>
          ))}
          <button
            onClick={handleDrillUp}
            className="ml-auto flex items-center gap-1 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('back')}
          </button>
        </div>
      )}
      
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

export default TopModulesChart;
